import { Lock } from "../lib/lock";
import { PromiseCompletionSource } from "../lib/task_completion";
import { ClientInfo } from "../model/client_info";
import { Client } from "../model/client_model";
import { MessageModel } from "../model/message";
import { ClientLeave } from "../model/messages/ClientLeave";
import { NewClientJoinedToRoom } from "../model/messages/NewClientJoined";
import { SessionDescription } from "../model/session_description";
import { Signaling } from "./interface";

/** @internal */
enum Issues {
  ClientInfo = "client_id",
  LeaveClient = "leave_client",
  NewClientJoinedToRoom = "new_client",
  JoinedToRoom = "join_room",
  ServerOffer = "server_offer",
  ServerAnswer = "server_answer",
  Broadcast = "broadcast",
}

/** @internal */
export class WebSocketSignaling implements Signaling {
  //#region Events

  OnModuleData: ((issue: string, data: any) => void) | undefined;

  OnServerSendOffer: ((sdp: SessionDescription) => void) | undefined;
  OnServerSendAnswer: ((sdp: SessionDescription) => void) | undefined;
  OnNewClientJoined: ((client: Client) => void) | undefined;
  OnClientLeave: ((client: Client) => void) | undefined;
  OnClientChangeMicrophoneStatus:
    | ((enabled: boolean, client: Client) => void)
    | undefined;

  OnCloseConnection: (() => void) | undefined;
  OnJoinedInRoom: ((room: NewClientJoinedToRoom) => void) | undefined;

  OnBroadcastReceived: ((clientId: string, data: any) => void) | undefined;

  //#endregion

  private _clientsLock: Lock;
  private readonly _url: string;
  private _clientInfo?: ClientInfo;
  // private _myId: string | undefined;
  private _clients: Array<Client> = [];
  private _wsConnection: WebSocket | undefined;
  private _connectionPromiseSource:
    | PromiseCompletionSource<ClientInfo>
    | undefined;

  constructor(
    address: string,
    token: string,
    port: number,
    url: string,
    protocol: "ws" | "wss"
  ) {
    this._clientsLock = new Lock();
    const tokenQuery = url.includes("?")
      ? `&token=${token}`
      : `?token=${token}`;

    this._url = `${protocol}://${address}${
      ![443, 80].includes(port) ? `:${port}` : ""
    }${url}${tokenQuery}`;
  }

  BroadcastMessage(data: any): void {
    this.SendMessage({ issue: "broadcast", value: data });
  }

  get Clients(): ReadonlyArray<Client> {
    return this._clients;
  }

  Close(): void {
    if (this._wsConnection) this._wsConnection.close();
  }

  Connect(): Promise<ClientInfo> {
    this._wsConnection = new WebSocket(this._url);
    this._connectionPromiseSource = new PromiseCompletionSource();

    this._wsConnection.addEventListener("message", async (event) => {
      await this.OnMessage(event.data);
    });
    this._wsConnection.addEventListener("open", () => {});
    this._wsConnection.addEventListener("close", () => {
      if (this._connectionPromiseSource !== undefined) {
        this._connectionPromiseSource.Reject("failed to connect");
      } else {
        if (this.OnCloseConnection) this.OnCloseConnection();
      }
    });
    this._wsConnection.addEventListener("error", () => {
      if (this._connectionPromiseSource !== undefined) {
        this._connectionPromiseSource.Reject("failed to connect");
      } else {
        if (this.OnCloseConnection) this.OnCloseConnection();
      }
    });

    return this._connectionPromiseSource.Promise;
  }

  SendMyOffer(sdp: SessionDescription): void {
    const msg = new MessageModel();
    msg.issue = "my_offer";
    msg.value = sdp;

    this.SendMessage(msg);
  }

  SendMyAnswer(sdp: SessionDescription): void {
    const msg = new MessageModel();
    msg.issue = "my_answer";
    msg.value = sdp;

    this.SendMessage(msg);
  }

  SendMyIceCandidate(ice: RTCIceCandidate): void {
    const msg = new MessageModel();
    msg.issue = "ice";
    msg.value = {
      sdp: ice.candidate,
      sdpMid: ice.sdpMid,
      sdpMLineIndex: ice.sdpMLineIndex,
    };

    this.SendMessage(msg);
  }

  DisableLiveTranslation(): void {
    this.SendMessage({
      issue: "live_translate",
      value: { status: false },
    });
  }

  EnableLiveTranslation(srcLang: string, dst: string): void {
    this.SendMessage({
      issue: "live_translate",
      value: { status: true, src: srcLang, dst: dst },
    });
  }

  DisableCaption(): void {
    this.SendMessage({
      issue: "caption",
      value: { state: "off" },
    });
  }
  EnableCaption(
    state: "caption" | "translate",
    srcLang: string,
    dst: string
  ): void {
    this.SendMessage({
      issue: "caption",
      value: { state, srcLang, dst },
    });
  }

  private async OnMessage(message: string): Promise<void> {
    try {
      let model = JSON.parse(message) as MessageModel;

      switch (model.issue) {
        case Issues.ClientInfo:
          this.OnWebSocketClientInfoEventCalled(model.value as ClientInfo);
          break;

        case Issues.ServerAnswer:
          if (!!this.OnServerSendAnswer) {
            this.OnServerSendAnswer(SessionDescription.Load(model.value));
          }
          break;

        case Issues.ServerOffer:
          if (this.OnServerSendOffer) {
            this.OnServerSendOffer(SessionDescription.Load(model.value));
          }
          break;

        case Issues.NewClientJoinedToRoom:
          await this.OnWebSocketNewClientJoinedEventCalled(
            model.value as Client
          );
          break;

        case Issues.LeaveClient:
          await this.OnWebSocketClientLeaveEventCalled(model.value as Client);
          break;

        case Issues.JoinedToRoom:
          await this.JoinToRoomCompleted(model.value as NewClientJoinedToRoom);
          break;

        case Issues.Broadcast:
          this.OnBroadcastReceived(model.value.clientId, model.value.data);
          break;

        default:
          if (this.OnModuleData) this.OnModuleData(model.issue, model.value);
          break;
      }
    } catch (error) {
      console.error(error);
    }
  }

  //#region WebSocket Events

  private OnWebSocketClientInfoEventCalled(info: ClientInfo): void {
    // if (this._connectionPromiseSource) {
    //     this._connectionPromiseSource.Resolve(info);
    // } else {
    //     throw new Error("Connection promise is null or undefined.");
    // }
    this._clientInfo = info;
  }

  private async OnWebSocketNewClientJoinedEventCalled(model: Client) {
    await this._clientsLock.acquire();

    // this._myId = model.id;
    try {
      // this._clients = model.clients as Array<Client>;
      this._clients.push(model);
    } catch (error) {
      console.error(error);
    } finally {
      this._clientsLock.release();

      if (this.OnNewClientJoined /*&& this._myId != model.id*/) {
        // const client = model.clients!.find((x) => x.id == model.id)!;
        this.OnNewClientJoined(model);
      }
    }
  }

  private async OnWebSocketClientLeaveEventCalled(model: Client) {
    await this._clientsLock.acquire();
    const client = this._clients.find((x) => x.id == model.id)!;

    try {
      this._clients = this._clients.filter((x) => x.id != model.id);
    } catch (error) {
      console.error(error);
    } finally {
      this._clientsLock.release();
    }

    if (this.OnClientLeave) {
      this.OnClientLeave(client);
    }
  }

  private async JoinToRoomCompleted(room: NewClientJoinedToRoom) {
    if (this._connectionPromiseSource) {
      await this._clientsLock.acquire();

      // this._myId = model.id;
      try {
        // this._clients = model.clients as Array<Client>;
        this._clients = room.clients;
      } catch (error) {
        console.error(error);
      } finally {
        this._clientsLock.release();
        this._connectionPromiseSource.Resolve(this._clientInfo);
      }
    } else {
      throw new Error("Connection promise is null or undefined.");
    }
  }

  //#endregion

  private SendMessage(msg: MessageModel) {
    this._wsConnection?.send(JSON.stringify(msg));
  }
}
